import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Divider,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteInvoiceTransaction } from "../../services/InvoiceService";
import CommonLoader from "../common/CommonLoader";
import DeleteConfirmation from "../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import AddEditInvoiceTransactionDialog from "./AddEditInvoiceTransactionDialog";

const InvoiceTransactionsTable = ({ transactions,orderNumber }) => {
  const columns = [
    "Invoice Number",
    "GL Code",
    "Description",
    "Quantity",
    "Unit Price",
    "List Price",
    "Adjusted Price",    
    "Actions",
  ];

  const columnHeaders = [
    "invoiceNumber",
    "glCode",
    "description",
    "quantity",
    "unitPrice",
    "listPrice",
    "adjustedPrice",    
  ];

  const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];
  const [filteredData, setFilteredData] = useState(transactions);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedTransactionToDelete, setSelectedTransactionToDelete] =
    useState(null);
  const [isAddEditDialogOpen, setIsAddEditDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setFilteredData(transactions);
  }, [transactions]);

  useEffect(() => {
    const filteredResults = transactions.filter((transaction) =>
      columnHeaders.some((header) =>
        transaction[header]
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
  }, [searchTerm, transactions]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (transaction) => {
    setIsEditing(true)
    setSelectedTransaction(transaction);
    setIsAddEditDialogOpen(true); // Open the dialog in "edit" mode
  };

  const handleAddClick = () => {    
    const maxRecordTransaction = transactions.reduce(
      (max, transaction) =>
        transaction.recordid > max.recordid ? transaction : max,
      transactions[0]
    );

    // Create a copy of the transaction and set recordid to 0
    const transactionWithZeroRecordId = {
      ...maxRecordTransaction,
      recordid: 0,
      orderNumber:orderNumber
    }; 
    setSelectedTransaction(transactionWithZeroRecordId);
    setIsAddEditDialogOpen(true);
  };

  const handleDeleteClick = (transaction) => {
    setSelectedTransactionToDelete(transaction);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteInvoiceTransaction(selectedTransactionToDelete.invoiceNumber,selectedTransactionToDelete.recordid);
      NotificationManager.success("Invoice Transaction deleted successfully");
      window.location.reload();
    } catch (error) {
      console.error("Error deleting Invoice Transaction:", error);
      NotificationManager.error("Error deleting Invoice Transaction");
    } finally {
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleAddEditDialogClose = () => {
    setIsAddEditDialogOpen(false);
    setIsEditing(false)
    setSelectedTransaction(null);
  };

  const handleAddEditDialogSuccess = () => {
    setIsAddEditDialogOpen(false);
    setSelectedTransaction(null);
    setIsEditing(false)
    window.location.reload();
  };

  const renderActions = (transaction) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(transaction)} // Edit button action
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(transaction)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <Paper elevation={3} sx={{ padding: 3 }}>
      <Typography variant="h13" style={{ color: "rgb(25,118,210)" }}>
        Invoice Transactions
      </Typography>

      <Divider sx={{ mb: 1 }} />
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddClick}
        sx={{ mb: 2 }}
      >
        New
      </Button>
      <TableContainer>
        <Table sx={{ minWidth: 400 }} size="small">
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((transaction, index) => (
                <TableRow
                  key={transaction.recordid}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                  }}
                >
                  {columnHeaders.map((column) => (
                    <TableCell key={column}>
                      {typeof transaction[column] === "boolean"
                        ? transaction[column]
                          ? "Yes"
                          : "No"
                        : transaction[column]}
                    </TableCell>
                  ))}
                  <TableCell>{renderActions(transaction)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={transactions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this transaction?"
        color="red"
      />
      {loading && <CommonLoader />}
      {isAddEditDialogOpen && (
        <AddEditInvoiceTransactionDialog
          open={isAddEditDialogOpen}
          onClose={handleAddEditDialogClose}
          onSave={handleAddEditDialogSuccess}
          selectedTransaction={selectedTransaction}
          isEditing={isEditing}
        />
      )}
    </Paper>
  );
};

export default InvoiceTransactionsTable;
